<template>
  <div class="card">
    <div class="card-header d-flex align-items-center justify-content-between">
      <div>
        <h4 class="card-title mb-0">Правила возврата</h4>
        <div v-if="startDate" class="text-muted small mt-25">
          Дата старта: {{ formattedStartDate }}
        </div>
      </div>
      <div class="d-flex align-items-center gap-50">
        <b-button
          v-if="effective.source === 'global'"
          variant="primary"
          :disabled="loading || cloning"
          @click="cloneFromGlobal"
        >
          <b-spinner v-if="cloning" small class="mr-50" />
          Скопировать и редактировать
        </b-button>
        <b-button
          v-if="effective.source === 'start'"
          variant="primary"
          :disabled="loading"
          @click="openCreate"
        >
          Добавить правило
        </b-button>
      </div>
    </div>

    <div class="card-body">
      <b-alert v-if="errorText" show variant="danger" class="mb-1">
        {{ errorText }}
      </b-alert>

      <b-alert v-if="effective.source === 'global'" show variant="info" class="mb-1">
        Сейчас применяются <strong>глобальные</strong> правила. Чтобы настроить правила для этого старта,
        нажмите “Скопировать и редактировать”.
      </b-alert>

      <b-table
        :items="rules"
        :fields="fields"
        striped
        responsive
        small
        :busy="loading"
        empty-text="Правила не найдены"
      >
        <template #cell(refund_percent)="data">
          {{ data.item.refund_percent }}%
        </template>

        <template #cell(description)="data">
          <span v-if="data.item.description">{{ data.item.description }}</span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(actions)="data">
          <div v-if="effective.source === 'start'" class="d-flex align-items-center gap-50">
            <b-button size="sm" variant="outline-primary" @click="openEdit(data.item)">
              Редактировать
            </b-button>
            <b-button size="sm" variant="outline-danger" @click="confirmDelete(data.item)">
              Удалить
            </b-button>
          </div>
          <span v-else class="text-muted">—</span>
        </template>

        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner small class="align-middle mr-1" />
            <strong>Загрузка...</strong>
          </div>
        </template>
      </b-table>
    </div>

    <b-modal
      id="start-refund-rule-modal"
      ref="ruleModal"
      :title="editingRule ? 'Редактирование правила' : 'Новое правило'"
      ok-title="Сохранить"
      cancel-title="Отмена"
      :ok-disabled="saving || !isFormValid"
      :cancel-disabled="saving"
      @ok="save"
    >
      <b-form @submit.prevent>
        <b-form-group label="Дней до старта" label-for="start_days_before_start">
          <b-form-input
            id="start_days_before_start"
            v-model.number="form.days_before_start"
            type="number"
            min="0"
            step="1"
            :disabled="saving"
            required
          />
        </b-form-group>

        <b-form-group label="Процент возврата" label-for="start_refund_percent">
          <b-form-input
            id="start_refund_percent"
            v-model.number="form.refund_percent"
            type="number"
            min="0"
            max="100"
            step="1"
            :disabled="saving"
            required
          />
        </b-form-group>

        <b-form-group label="Описание" label-for="start_description">
          <b-form-textarea
            id="start_description"
            v-model.trim="form.description"
            rows="3"
            max-rows="6"
            :disabled="saving"
            placeholder="Опционально"
          />
        </b-form-group>
      </b-form>
    </b-modal>

    <b-modal
      id="start-refund-rule-delete"
      ref="deleteModal"
      title="Удалить правило?"
      ok-title="Удалить"
      ok-variant="danger"
      cancel-title="Отмена"
      :ok-disabled="saving"
      :cancel-disabled="saving"
      @ok="deleteRule"
    >
      <div>
        <div class="mb-50">
          Вы уверены, что хотите удалить правило:
        </div>
        <div class="small">
          <strong>{{ deletingRulePreview }}</strong>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'RefundRulesSection',
  components: {
    BAlert,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BModal,
    BSpinner,
    BTable,
  },
  props: {
    startId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      cloning: false,
      errorText: '',
      effective: {
        source: null,
        start_id: null,
        start_date: null,
        rules: [],
      },
      localRules: [],
      editingRule: null,
      deletingRule: null,
      form: {
        days_before_start: 0,
        refund_percent: 0,
        description: '',
      },
      fields: [
        { key: 'days_before_start', label: 'Дней до старта', sortable: true },
        { key: 'refund_percent', label: 'Возврат, %', sortable: true },
        { key: 'description', label: 'Описание' },
        { key: 'actions', label: 'Действия' },
      ],
    }
  },
  computed: {
    rules() {
      if (this.effective.source === 'start') return this.normalizeRules(this.localRules)
      return this.normalizeRules(this.effective.rules)
    },
    startDate() {
      return this.effective.start_date
    },
    formattedStartDate() {
      if (!this.startDate) return ''
      try {
        return this.$moment(this.startDate).format('DD.MM.YYYY')
      } catch (e) {
        return this.startDate
      }
    },
    isFormValid() {
      const d = this.form.days_before_start
      const p = this.form.refund_percent
      return Number.isFinite(d) && d >= 0 && Number.isFinite(p) && p >= 0 && p <= 100
    },
    deletingRulePreview() {
      if (!this.deletingRule) return ''
      const desc = this.deletingRule.description ? ` — ${this.deletingRule.description}` : ''
      return `${this.deletingRule.days_before_start} дней → ${this.deletingRule.refund_percent}%${desc}`
    },
  },
  async created() {
    await this.refresh()
  },
  methods: {
    toastSuccess(text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Успешно',
          icon: 'BellIcon',
          text,
          variant: 'success',
        },
      })
    },
    toastError(text, title = 'Ошибка') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: 'AlertTriangleIcon',
          text,
          variant: 'danger',
        },
      })
    },
    getErrorMessage(error) {
      const data = error?.response?.data
      if (!data) return 'Не удалось выполнить запрос'
      if (typeof data === 'string') return data
      if (data.message) return data.message
      return 'Не удалось выполнить запрос'
    },
    isDuplicateDaysBeforeStartError(error) {
      const status = error?.response?.status
      if (status !== 400) return false

      const data = error?.response?.data
      const raw = typeof data === 'string'
        ? data
        : (data?.message || JSON.stringify(data || ''))

      const text = String(raw).toLowerCase()
      return text.includes('days_before_start')
        || text.includes('days before start')
        || (text.includes('days') && text.includes('before') && text.includes('start'))
        || (text.includes('уже') && (text.includes('такой') || text.includes('одинаков')) && text.includes('правил'))
        || (text.includes('duplicate') && (text.includes('days') || text.includes('before')))
    },
    normalizeRules(list) {
      const arr = Array.isArray(list) ? list : []
      return [...arr].sort((a, b) => (b.days_before_start ?? 0) - (a.days_before_start ?? 0))
    },
    async refresh() {
      await this.fetchEffective()
      if (this.effective.source === 'start') {
        await this.fetchLocalRules()
      } else {
        this.localRules = []
      }
    },
    async fetchEffective() {
      this.loading = true
      this.errorText = ''
      try {
        const { data } = await this.$http.get(`start/${this.startId}/refund-rules/effective`)
        this.effective = data || { source: null, rules: [] }
      } catch (e) {
        this.errorText = this.getErrorMessage(e)
      } finally {
        this.loading = false
      }
    },
    async fetchLocalRules() {
      this.loading = true
      this.errorText = ''
      try {
        const { data } = await this.$http.get(`start/${this.startId}/refund-rules`)
        this.localRules = Array.isArray(data) ? data : (data?.rows || data?.rules || [])
      } catch (e) {
        this.errorText = this.getErrorMessage(e)
      } finally {
        this.loading = false
      }
    },
    async cloneFromGlobal() {
      if (this.cloning || this.loading) return
      this.cloning = true
      try {
        await this.$http.post(`start/${this.startId}/refund-rules/clone-from-global`)
        this.toastSuccess('Глобальные правила скопированы')
        await this.refresh()
      } catch (e) {
        const status = e?.response?.status
        const msg = this.getErrorMessage(e)
        if (this.isDuplicateDaysBeforeStartError(e)) {
          this.toastError(
            'Нельзя создать два одинаковых правила по “дней до старта”. Измените значение “Дней до старта”.',
            'Ошибка',
          )
        } else {
          this.toastError(msg, status === 400 ? 'Ошибка валидации' : 'Ошибка')
        }
      } finally {
        this.cloning = false
      }
    },
    resetForm() {
      this.form = {
        days_before_start: 0,
        refund_percent: 0,
        description: '',
      }
    },
    openCreate() {
      this.editingRule = null
      this.resetForm()
      this.$refs.ruleModal.show()
    },
    openEdit(rule) {
      this.editingRule = rule
      this.form = {
        days_before_start: rule.days_before_start,
        refund_percent: rule.refund_percent,
        description: rule.description || '',
      }
      this.$refs.ruleModal.show()
    },
    async save(bvModalEvt) {
      bvModalEvt.preventDefault()
      if (!this.isFormValid || this.saving) return
      this.saving = true
      try {
        if (!this.editingRule) {
          await this.$http.post(`start/${this.startId}/refund-rules`, {
            refund_percent: this.form.refund_percent,
            days_before_start: this.form.days_before_start,
            description: this.form.description || undefined,
          })
          this.toastSuccess('Правило создано')
        } else {
          await this.$http.patch(`start/${this.startId}/refund-rules/${this.editingRule.id}`, {
            refund_percent: this.form.refund_percent,
            days_before_start: this.form.days_before_start,
            description: this.form.description || undefined,
          })
          this.toastSuccess('Правило обновлено')
        }
        await this.refresh()
        this.$refs.ruleModal.hide()
      } catch (e) {
        const status = e?.response?.status
        const msg = this.getErrorMessage(e)
        if (this.isDuplicateDaysBeforeStartError(e)) {
          this.toastError(
            'Нельзя создать два одинаковых правила по “дней до старта”. Измените значение “Дней до старта”.',
            'Ошибка',
          )
        } else {
          this.toastError(msg, status === 400 ? 'Ошибка валидации' : 'Ошибка')
        }
      } finally {
        this.saving = false
      }
    },
    confirmDelete(rule) {
      this.deletingRule = rule
      this.$refs.deleteModal.show()
    },
    async deleteRule(bvModalEvt) {
      bvModalEvt.preventDefault()
      if (!this.deletingRule || this.saving) return
      this.saving = true
      try {
        await this.$http.delete(`start/${this.startId}/refund-rules/${this.deletingRule.id}`)
        this.toastSuccess('Правило удалено')
        await this.refresh()
        this.$refs.deleteModal.hide()
      } catch (e) {
        this.toastError(this.getErrorMessage(e))
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.gap-50 {
  gap: 0.5rem;
}
</style>
